import { cubaWallTimeToUtc } from '../time/cubaTime';

export type RouteDepartMode = 'manual' | 'scheduled';
export type RouteDepartureStatus = 'pending' | 'authorized';

export interface RouteDepartConfig {
  routeDepartMode?: RouteDepartMode | string;
  routeDepartAt?: string;
  routeDepartMarginMin?: number;
}

export interface RouteDepartureState {
  departureStatus?: RouteDepartureStatus | string | null;
  authorizedAt?: number | null;
  authorizedBy?: string | null;
  autoAuthorizeAt?: number | null;
}

const DEFAULT_DEPART_AT = '18:00';

export function normalizeRouteDepartMode(raw: unknown): RouteDepartMode {
  return raw === 'scheduled' ? 'scheduled' : 'manual';
}

export function parseDepartAtMinutes(hhmm: string | undefined): number {
  const m = String(hhmm ?? DEFAULT_DEPART_AT).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return 18 * 60;
  const h = Math.min(23, Math.max(0, Number(m[1])));
  const min = Math.min(59, Math.max(0, Number(m[2])));
  return h * 60 + min;
}

/**
 * Scheduled auto-authorize instant: Cuba wall time on dayKey + uniform random
 * delay in [0, marginMin] minutes (never before the base clock time).
 */
export function computeAutoAuthorizeAt(
  dayKey: string,
  routeDepartAt: string | undefined,
  marginMin: number,
  random: () => number = Math.random,
): number {
  const [y, mo, d] = dayKey.split('-').map(Number);
  const mins = parseDepartAtMinutes(routeDepartAt);
  const base = cubaWallTimeToUtc(y, mo, d, Math.floor(mins / 60), mins % 60, 0);
  const margin = Math.max(0, Math.floor(Number(marginMin) || 0));
  const delayMin = margin <= 0 ? 0 : Math.floor(random() * (margin + 1));
  return base.getTime() + delayMin * 60_000;
}

export function isRouteDepartureAuthorized(
  route: RouteDepartureState | null | undefined,
): boolean {
  if (!route) return false;
  if (route.departureStatus === 'authorized') return true;
  if (typeof route.authorizedAt === 'number' && route.authorizedAt > 0) return true;
  // Soft unlock via autoAuthorizeAt removed — hard auth comes from scheduler / ensure callable.
  return false;
}

export function shouldPersistAutoAuthorization(
  route: RouteDepartureState | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!route) return false;
  if (route.departureStatus === 'authorized') return false;
  if (typeof route.autoAuthorizeAt !== 'number' || route.autoAuthorizeAt <= 0) return false;
  return nowMs >= route.autoAuthorizeAt;
}

export function canRunnerConfirmStop(route: RouteDepartureState | null | undefined): boolean {
  return isRouteDepartureAuthorized(route);
}

export function canListerConfirmHandoff(route: RouteDepartureState | null | undefined): boolean {
  return isRouteDepartureAuthorized(route);
}

export function buildAuthorizedDeparturePatch(actorId: string, nowMs: number = Date.now()) {
  return {
    departureStatus: 'authorized' as const,
    authorizedAt: nowMs,
    authorizedBy: actorId,
    collectionStatus: 'in_progress' as const,
    completedAt: null,
    completedBy: null,
  };
}
